import * as tslib_1 from "tslib";
import { EvalStack, Item, equal } from '@swim/recon';
import { toAttributeString, toStyleString } from './style';
import { Ease } from './ease';
import { Transition } from './transition';
export var TweenState;
(function (TweenState) {
    TweenState[TweenState["Quiesced"] = 0] = "Quiesced";
    TweenState[TweenState["Diverged"] = 1] = "Diverged";
    TweenState[TweenState["Tracking"] = 2] = "Tracking";
    TweenState[TweenState["Converged"] = 3] = "Converged";
    TweenState[TweenState["Interrupt"] = 4] = "Interrupt";
})(TweenState || (TweenState = {}));
var Animator = (function () {
    function Animator() {
    }
    Animator.DISABLED = 1 << 0;
    Animator.REQUIRES_RESIZE = 1 << 4;
    return Animator;
}());
export { Animator };
var FrameAnimator = (function (_super) {
    tslib_1.__extends(FrameAnimator, _super);
    function FrameAnimator() {
        var _this = _super.call(this) || this;
        _this.flags = 0;
        _this.animationFrame = 0;
        _this.onAnimationFrame = _this.onAnimationFrame.bind(_this);
        return _this;
    }
    FrameAnimator.prototype.enabled = function (enabled) {
        if (enabled === undefined) {
            return (this.flags & Animator.DISABLED) === 0;
        }
        else {
            if (enabled && (this.flags & Animator.DISABLED) !== 0) {
                this.flags &= ~Animator.DISABLED;
                this.animate();
            }
            else if (!enabled && (this.flags & Animator.DISABLED) === 0) {
                this.flags |= Animator.DISABLED;
            }
            return this;
        }
    };
    FrameAnimator.prototype.animate = function () {
        if (!this.animationFrame && (this.flags & Animator.DISABLED) === 0) {
            this.animationFrame = requestAnimationFrame(this.onAnimationFrame);
        }
    };
    FrameAnimator.prototype.cancel = function () {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = 0;
        }
    };
    FrameAnimator.prototype.onAnimationFrame = function (timestamp) {
        this.animationFrame = 0;
        this.onFrame(timestamp);
    };
    return FrameAnimator;
}(Animator));
export { FrameAnimator };
var ChildAnimator = (function (_super) {
    tslib_1.__extends(ChildAnimator, _super);
    function ChildAnimator(parent) {
        var _this = _super.call(this) || this;
        _this.parent = parent;
        return _this;
    }
    ChildAnimator.prototype.animate = function () {
        this.parent.animate();
    };
    return ChildAnimator;
}(Animator));
export { ChildAnimator };
var TweenAnimator = (function (_super) {
    tslib_1.__extends(TweenAnimator, _super);
    function TweenAnimator(value, transition, flags) {
        if (flags === void 0) { flags = 0; }
        var _this = _super.call(this) || this;
        if (!transition.f) {
            throw new Error('no interpolator');
        }
        _this.dt = transition.dt !== undefined ? transition.dt : 0;
        _this.ft = transition.ft || Ease.linear;
        _this.f = transition.f;
        _this.f0 = transition.f0;
        _this.f1 = transition.f1;
        _this.fi = transition.fi;
        _this.fl = undefined;
        _this.v = value;
        _this.v1 = value;
        _this.t0 = 0;
        _this.flags = flags;
        _this.tweenState = 0;
        return _this;
    }
    TweenAnimator.prototype.enabled = function (enabled) {
        if (enabled === undefined) {
            return (this.flags & Animator.DISABLED) === 0;
        }
        else {
            if (enabled && (this.flags & Animator.DISABLED) !== 0) {
                this.flags &= ~Animator.DISABLED;
                this.animate();
            }
            else if (!enabled && (this.flags & Animator.DISABLED) === 0) {
                this.flags |= Animator.DISABLED;
            }
            return this;
        }
    };
    TweenAnimator.prototype.transition = function (transition) {
        if (!transition) {
            return new Transition(this.dt, this.ft, this.f);
        }
        else {
            if (transition.dt !== undefined) {
                this.dt = transition.dt;
            }
            if (transition.ft) {
                this.ft = transition.ft;
            }
            if (transition.f) {
                this.f = transition.f;
            }
            if (transition.f0 !== undefined) {
                this.f0 = transition.f0;
            }
            if (transition.f1 !== undefined) {
                this.f1 = transition.f1;
            }
            if (transition.fi !== undefined) {
                this.fi = transition.fi;
            }
            return this;
        }
    };
    TweenAnimator.prototype.duration = function (dt) {
        if (dt === undefined) {
            return this.dt;
        }
        else {
            this.dt = Math.max(0, dt);
            return this;
        }
    };
    TweenAnimator.prototype.ease = function (ft) {
        if (!ft) {
            return this.ft;
        }
        else {
            this.ft = Ease.fromAny(ft);
            return this;
        }
    };
    TweenAnimator.prototype.get = function () {
        return this.v;
    };
    TweenAnimator.prototype.set = function (value, transition) {
        var fl = this.fi;
        this.fi = undefined;
        if (transition) {
            this.transition(transition);
        }
        this.f = this.f.range(this.v, value);
        this.fl = fl;
        this.v1 = value;
        this.t0 = 0;
        if (this.tweenState === 2) {
            this.tweenState = 4;
        }
        else {
            this.tweenState = 1;
        }
        if (transition) {
            this.animate();
        }
        else {
            this.onFrame(0);
        }
    };
    TweenAnimator.prototype.onFrame = function (t) {
        if (this.tweenState === 0 || (this.flags & Animator.DISABLED) !== 0) {
            return;
        }
        if (this.tweenState === 4) {
            this.onInterrupt(this.v);
            if (this.fl) {
                this.fl(this.v);
                this.fl = undefined;
            }
            this.tweenState = 1;
        }
        if (this.tweenState === 1) {
            if (!equal(this.v, this.v1)) {
                this.t0 = t;
                this.onStart(this.v);
                if (this.f0) {
                    this.f0(this.v);
                    this.f0 = undefined;
                }
                this.tweenState = 2;
            }
            else {
                this.tween(1);
            }
        }
        if (this.tweenState === 2) {
            var u = this.dt ? Math.min(Math.max(0, (t - this.t0) / this.dt), 1) : 1;
            this.tween(u);
        }
        if (this.tweenState === 3) {
            this.onEnd(this.v);
            if (this.f1) {
                this.f1(this.v);
                this.f1 = undefined;
            }
            this.fi = undefined;
            this.t0 = 0;
            this.tweenState = 0;
        }
        else {
            this.animate();
        }
    };
    TweenAnimator.prototype.map = function (u) {
        return this.f.map(u);
    };
    TweenAnimator.prototype.tween = function (u) {
        u = this.ft(u);
        this.v = this.map(u);
        this.update(this.v);
        if (u === 1) {
            this.tweenState = 3;
        }
    };
    TweenAnimator.prototype.updateTarget = function (target) {
        var view = target.view;
        if (view) {
            if ((this.flags & Animator.REQUIRES_RESIZE) !== 0) {
                view.requireResize();
            }
        }
    };
    TweenAnimator.prototype.onStart = function (value) {
    };
    TweenAnimator.prototype.onEnd = function (value) {
    };
    TweenAnimator.prototype.onInterrupt = function (value) {
    };
    return TweenAnimator;
}(Animator));
export { TweenAnimator };
var ChildFrameAnimator = (function (_super) {
    tslib_1.__extends(ChildFrameAnimator, _super);
    function ChildFrameAnimator(parent, value, transition, flags) {
        var _this = _super.call(this, value, transition, flags) || this;
        _this.parent = parent;
        return _this;
    }
    ChildFrameAnimator.prototype.animate = function () {
        if ((this.flags & Animator.DISABLED) === 0) {
            this.parent.animate();
        }
    };
    return ChildFrameAnimator;
}(TweenAnimator));
export { ChildFrameAnimator };
var TweenFrameAnimator = (function (_super) {
    tslib_1.__extends(TweenFrameAnimator, _super);
    function TweenFrameAnimator(value, transition, flags) {
        var _this = _super.call(this, value, transition, flags) || this;
        _this.animationFrame = 0;
        _this.onAnimationFrame = _this.onAnimationFrame.bind(_this);
        return _this;
    }
    TweenFrameAnimator.prototype.animate = function () {
        if (!this.animationFrame && (this.flags & Animator.DISABLED) === 0) {
            this.animationFrame = requestAnimationFrame(this.onAnimationFrame);
        }
    };
    TweenFrameAnimator.prototype.cancel = function () {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = 0;
        }
    };
    TweenFrameAnimator.prototype.onAnimationFrame = function (timestamp) {
        this.animationFrame = 0;
        this.onFrame(timestamp);
    };
    return TweenFrameAnimator;
}(TweenAnimator));
export { TweenFrameAnimator };
var AttributeAnimator = (function (_super) {
    tslib_1.__extends(AttributeAnimator, _super);
    function AttributeAnimator(target, name, value, transition, flags) {
        var _this = _super.call(this, value, transition, flags) || this;
        _this.target = target;
        _this.name = name;
        return _this;
    }
    AttributeAnimator.prototype.update = function (value) {
        var oldValue = this.target.getAttribute(this.name);
        var newValue = toAttributeString(value);
        if (oldValue !== newValue) {
            this.target.setAttribute(this.name, newValue);
            this.updateTarget(this.target);
        }
    };
    AttributeAnimator.prototype.animate = function () {
        _super.prototype.animate.call(this);
    };
    return AttributeAnimator;
}(TweenFrameAnimator));
export { AttributeAnimator };
var StyleAnimator = (function (_super) {
    tslib_1.__extends(StyleAnimator, _super);
    function StyleAnimator(target, name, value, transition, priority, flags) {
        var _this = _super.call(this, value, transition, flags) || this;
        _this.target = target;
        _this.name = name;
        _this.priority = priority;
        return _this;
    }
    StyleAnimator.prototype.update = function (value) {
        var oldValue = this.target.style.getPropertyValue(this.name);
        var newValue = toStyleString(value);
        if (oldValue !== newValue) {
            this.target.style.setProperty(this.name, newValue, this.priority);
            this.updateTarget(this.target);
        }
    };
    StyleAnimator.prototype.animate = function () {
        _super.prototype.animate.call(this);
    };
    return StyleAnimator;
}(TweenFrameAnimator));
export { StyleAnimator };
var TextAnimator = (function (_super) {
    tslib_1.__extends(TextAnimator, _super);
    function TextAnimator(target, value, transition, format, flags) {
        var _this = _super.call(this, value, transition, flags) || this;
        _this.target = target;
        _this.format = format;
        return _this;
    }
    TextAnimator.prototype.update = function (value) {
        var text;
        var format = this.format;
        if (typeof format === 'object') {
            format = format.format;
        }
        if (typeof format === 'function') {
            text = format(value);
        }
        else if (value instanceof Item) {
            var stack = void 0;
            if (this.target.view) {
                stack = this.target.view.stack();
            }
            else {
                stack = EvalStack.of(Item.GlobalScope);
            }
            text = value.evaluate(stack).stringValue('');
        }
        else {
            text = '' + value;
        }
        if (this.target instanceof Text) {
            this.target.nodeValue = text;
        }
        else if (this.target instanceof HTMLElement) {
            this.target.innerText = text;
        }
        else {
            this.target.textContent = text;
        }
    };
    TextAnimator.prototype.animate = function () {
        _super.prototype.animate.call(this);
    };
    return TextAnimator;
}(TweenFrameAnimator));
export { TextAnimator };
//# sourceMappingURL=data:application/json;base64,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